import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  PickingJobsOverviewDocument,
  type PickingJobsOverviewQuery,
  type PickingJobsOverviewQueryVariables,
} from '@/graphql/queries/pickingJobsOverview.generated';
import type { PickingJobQueryArgs } from './pickingJobFilters';
import { ageInDays } from './pickingJobRows';
import type { PickingJobRow, WarehouseOption } from './types';

type FanOutResult = {
  /** Which warehouses, queries and search term the result answers. */
  key: string;
  rows: PickingJobRow[];
  /** Warehouses whose query failed while others answered. */
  failedWarehouses: string[];
  /** Set only when every query failed. */
  error: Error | null;
};

// Shared so an empty result keeps one identity across renders.
const NO_ROWS: PickingJobRow[] = [];
const NO_WAREHOUSES: string[] = [];

/**
 * Loads the picking jobs of the selected warehouses.
 *
 * getPickingJobs refuses an unbounded filter — it needs a warehouse, an event
 * or a target store — and it is not paginated. Rather than change a query the
 * partner app depends on, the overview fans out one call per selected
 * warehouse (and per query in `queries`) and merges. There are two warehouses
 * today; if that number grows enough for the fan-out to hurt, the query grows
 * a paginated sibling instead.
 *
 * `search` is the applied picked-item search term, '' for none; it goes to
 * every query as `picked_item_search`.
 *
 * `onLoad` gets the rows of every load in which at least one query answered.
 * It is an effect dependency, so it must be stable.
 */
export function usePickingJobFanOut(
  selectedWarehouses: WarehouseOption[],
  queries: PickingJobQueryArgs[],
  search: string,
  onLoad: (rows: PickingJobRow[]) => void,
) {
  const client = useApolloClient();

  // Serialized so the effect does not re-run on fresh array identities.
  const warehouseKey = JSON.stringify(selectedWarehouses);
  const queriesKey = JSON.stringify(queries);
  const key = `${warehouseKey}|${queriesKey}|${search}`;

  const [result, setResult] = useState<FanOutResult | null>(null);

  useEffect(() => {
    const warehouses: WarehouseOption[] = JSON.parse(warehouseKey);
    const args: PickingJobQueryArgs[] = JSON.parse(queriesKey);
    // Nothing to ask (or the warehouses are still loading).
    if (warehouses.length === 0) return;

    // Filters can change faster than the fan-out answers: an older, slower
    // response must not replace the rows of the load that superseded it.
    let cancelled = false;
    const loadKey = `${warehouseKey}|${queriesKey}|${search}`;
    const tasks = warehouses.flatMap((warehouse) =>
      args.map((queryArgs) => ({ warehouse, queryArgs })),
    );

    void Promise.allSettled(
      tasks.map(async ({ warehouse, queryArgs }) => {
        const { data } = await client.query<
          PickingJobsOverviewQuery,
          PickingJobsOverviewQueryVariables
        >({
          query: PickingJobsOverviewDocument,
          variables: {
            item_location_id: warehouse.itemLocationId,
            pickup_date_from: queryArgs.from,
            pickup_date_to: queryArgs.to,
            statuses: queryArgs.statuses,
            // Omitted when there is no search; the backend treats a blank
            // term as unset too.
            picked_item_search: search || undefined,
          },
          fetchPolicy: 'network-only',
        });
        return (data?.getPickingJobs ?? []).map(
          (job): PickingJobRow => ({
            ...job,
            warehouseName: warehouse.name,
            ageInDays: ageInDays(job.carrier_pickup_date),
          }),
        );
      }),
    ).then((settled) => {
      if (cancelled) return;

      // The DELIVERED query and the one for the other statuses do not
      // overlap, but a job must never show twice whatever the split.
      const rowsById = new Map<string, PickingJobRow>();
      const failed = new Set<string>();
      let firstFailure: unknown = null;
      settled.forEach((settledTask, index) => {
        if (settledTask.status === 'fulfilled') {
          settledTask.value.forEach((row) => rowsById.set(row.id, row));
        } else {
          failed.add(tasks[index].warehouse.name);
          firstFailure ??= settledTask.reason;
        }
      });

      if (settled.every((settledTask) => settledTask.status === 'rejected')) {
        // Nothing loaded: the error alone says so, without the partial notice.
        setResult({
          key: loadKey,
          rows: [],
          failedWarehouses: [],
          error:
            firstFailure instanceof Error
              ? firstFailure
              : new Error(String(firstFailure)),
        });
        return;
      }

      const rows = [...rowsById.values()];
      setResult({
        key: loadKey,
        rows,
        failedWarehouses: [...failed],
        error: null,
      });
      onLoad(rows);
    });

    return () => {
      cancelled = true;
    };
  }, [client, warehouseKey, queriesKey, search, onLoad]);

  if (selectedWarehouses.length === 0) {
    return {
      rows: NO_ROWS,
      failedWarehouses: NO_WAREHOUSES,
      error: null,
      loading: false,
    };
  }
  // While a new load runs, the previous rows stay (they back the facet
  // options) but its error and partial-failure notice do not.
  const settled = result?.key === key ? result : null;
  return {
    rows: result?.rows ?? NO_ROWS,
    failedWarehouses: settled?.failedWarehouses ?? NO_WAREHOUSES,
    error: settled?.error ?? null,
    loading: settled === null,
  };
}
